import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Music2, Check, Loader2 } from "lucide-react";
import axios from "axios";

const N8N_WEBHOOK_URL =
  "https://n8n.synquic.in/webhook/ec348585-d5af-48aa-b675-78f8d1f774f9";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_f434a903-6e13-413a-a7d4-94188c08d9c5/artifacts/g9frieeg_Digital%20Worldwide%20Y2K%20Logo%20Template%20-%20Digital%20Worldwide%20Y2K%20Logo%20Template%2018-2.png";

/**
 * Diagonal Split Reveal:
 *  - Top-left triangle slice flies in from top-left.
 *  - Bottom-right triangle slice flies in from bottom-right.
 *  - Full logo fades in underneath to solidify the seam.
 */
const LogoAssemble = () => {
  return (
    <motion.div
      data-testid="logo-container"
      className="relative aspect-square w-[min(70vw,32vh,320px)] mx-auto select-none"
      initial={{ rotate: -3, scale: 0.96 }}
      animate={{ rotate: 0, scale: 1 }}
      transition={{ delay: 0.9, duration: 0.6, ease: "easeOut" }}
    >
      {/* Base full logo: fades in last to solidify seam */}
      <motion.img
        src={LOGO_URL}
        alt="Weepickly logo"
        data-testid="logo-base"
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.4, ease: "easeOut" }}
      />

      {/* Top-left triangular slice */}
      <motion.img
        src={LOGO_URL}
        alt=""
        aria-hidden="true"
        draggable={false}
        data-testid="logo-slice-tl"
        className="absolute inset-0 w-full h-full object-contain"
        style={{
          clipPath: "polygon(0 0, 100% 0, 0 100%)",
        }}
        initial={{ x: -160, y: -160, opacity: 0, rotate: -12 }}
        animate={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 180, damping: 18, delay: 0.15 }}
      />

      {/* Bottom-right triangular slice */}
      <motion.img
        src={LOGO_URL}
        alt=""
        aria-hidden="true"
        draggable={false}
        data-testid="logo-slice-br"
        className="absolute inset-0 w-full h-full object-contain"
        style={{
          clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
        }}
        initial={{ x: 160, y: 160, opacity: 0, rotate: 12 }}
        animate={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 180, damping: 18, delay: 0.15 }}
      />

      {/* Subtle continuous float */}
      <motion.div
        className="absolute inset-0"
        animate={{ y: [0, -6, 0] }}
        transition={{
          duration: 4.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.8,
        }}
      />
    </motion.div>
  );
};

const Tagline = () => (
  <motion.h1
    data-testid="tagline-text"
    className="font-fredoka text-2xl sm:text-3xl lg:text-4xl text-center leading-tight tracking-tight px-2"
    initial={{ y: 24, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ delay: 1.2, duration: 0.6, ease: "easeOut" }}
  >
    <span className="text-[#0B0B0B]">a little </span>
    <span className="text-[#3DA5E0] italic">picky</span>
    <span className="text-[#0B0B0B]">,</span>
    <br className="hidden sm:block" />
    <span className="text-[#0B0B0B]"> for the love of </span>
    <span className="text-[#26C44A]">pickleball</span>
    <span className="text-[#0B0B0B]">.</span>
  </motion.h1>
);

const SignupForm = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(N8N_WEBHOOK_URL, {
        name: name.trim(),
        phone: digits,
      });
      setStatus("success");
    } catch (err) {
      setError("Something went wrong. Please try again in a moment.");
      setStatus("idle");
    }
  };

  return (
    <motion.div
      className="w-full max-w-md"
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 1.55, type: "spring", stiffness: 110, damping: 15 }}
    >
      <div className="relative">
        {/* Sticker tag */}
        <div className="absolute -top-4 left-4 z-10 bg-[#F4D72E] border-2 border-[#0B0B0B] rounded-full px-3 py-0.5 font-fredoka text-[11px] sm:text-xs font-bold uppercase tracking-widest sticker-shadow-sm rotate-[-3deg]">
          Launch list
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="signup-form"
          className="w-full flex flex-col gap-4 bg-white p-6 sm:p-7 rounded-xl border-2 border-[#0B0B0B] sticker-shadow"
        >
          <AnimatePresence mode="wait">
            {status !== "success" ? (
              <motion.div
                key="form"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-4"
              >
                <div>
                  <label
                    htmlFor="name"
                    className="uppercase tracking-[0.12em] text-[11px] font-bold text-[#0B0B0B] mb-1.5 block font-outfit"
                  >
                    Your name
                  </label>
                  <input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Alex Morgan"
                    autoComplete="name"
                    maxLength={120}
                    className="w-full h-12 px-4 rounded-lg border-2 border-[#0B0B0B] bg-[#F2F1ED] focus:bg-white focus:outline-none focus:shadow-[2px_2px_0px_0px_#0B0B0B] transition-all font-outfit text-[#0B0B0B] font-medium placeholder-[#0B0B0B]/40"
                    disabled={status === "loading"}
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="uppercase tracking-[0.12em] text-[11px] font-bold text-[#0B0B0B] mb-1.5 block font-outfit"
                  >
                    Phone number
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-outfit text-[#0B0B0B] font-semibold pointer-events-none">
                      +91
                    </span>
                    <input
                      id="phone"
                      data-testid="phone-input"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) =>
                        setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      placeholder="98765 43210"
                      autoComplete="tel-national"
                      maxLength={10}
                      className="w-full h-12 pl-14 pr-4 rounded-lg border-2 border-[#0B0B0B] bg-[#F2F1ED] focus:bg-white focus:outline-none focus:shadow-[2px_2px_0px_0px_#0B0B0B] transition-all font-outfit text-[#0B0B0B] font-medium placeholder-[#0B0B0B]/40 tracking-wide"
                      disabled={status === "loading"}
                    />
                  </div>
                </div>

                {error ? (
                  <div
                    data-testid="form-error"
                    className="text-sm font-outfit font-medium text-[#0B0B0B] bg-[#F4D72E] border-2 border-[#0B0B0B] rounded-md px-3 py-2"
                  >
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  data-testid="submit-button"
                  disabled={status === "loading"}
                  className="w-full h-14 mt-1 rounded-lg border-2 border-[#0B0B0B] bg-[#3DA5E0] hover:bg-[#F4D72E] text-white hover:text-[#0B0B0B] font-fredoka text-lg sm:text-xl font-bold transition-all sticker-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_#0B0B0B] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Serving it up...
                    </>
                  ) : (
                    <>Notify me at launch</>
                  )}
                </button>

                <p className="text-[11px] font-outfit text-[#4A4A4A] text-center leading-relaxed">
                  We'll text you once. No spam. Unsubscribe anytime.
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                data-testid="success-message"
                initial={{ opacity: 0, y: 10, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                className="flex flex-col items-center text-center gap-3 py-4"
              >
                <div className="w-14 h-14 rounded-full bg-[#26C44A] border-2 border-[#0B0B0B] flex items-center justify-center sticker-shadow-sm">
                  <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </div>
                <div className="font-fredoka text-2xl font-bold text-[#0B0B0B]">
                  You're on the list!
                </div>
                <div className="font-outfit text-sm text-[#4A4A4A] max-w-xs">
                  Stay pickly,{" "}
                  <span className="font-semibold text-[#0B0B0B]">
                    {name.split(" ")[0] || "friend"}
                  </span>
                  . We'll ping your phone the moment we drop.
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </motion.div>
  );
};

const SocialRow = () => {
  const items = [
    {
      id: "instagram",
      label: "Instagram",
      href: "https://instagram.com",
      Icon: Instagram,
    },
    {
      id: "tiktok",
      label: "TikTok",
      href: "https://tiktok.com",
      Icon: Music2,
    },
  ];
  return (
    <motion.div
      className="flex gap-4 mt-2"
      initial="hidden"
      animate="show"
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: 0.12, delayChildren: 2.0 } },
      }}
    >
      {items.map(({ id, label, href, Icon }) => (
        <motion.a
          key={id}
          data-testid={`social-${id}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          variants={{
            hidden: { scale: 0, opacity: 0 },
            show: { scale: 1, opacity: 1 },
          }}
          transition={{ type: "spring", stiffness: 320, damping: 18 }}
          whileHover={{ y: -3 }}
          className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-[#0B0B0B] bg-white text-[#0B0B0B] hover:bg-[#26C44A] hover:text-white transition-colors sticker-shadow-sm"
        >
          <Icon className="w-5 h-5" />
        </motion.a>
      ))}
    </motion.div>
  );
};

const BackgroundDecor = () => (
  <>
    <div
      className="color-blob"
      style={{
        top: "-10%",
        left: "-8%",
        width: 380,
        height: 380,
        background: "#3DA5E0",
        opacity: 0.18,
      }}
    />
    <div
      className="color-blob"
      style={{
        bottom: "-12%",
        right: "-10%",
        width: 420,
        height: 420,
        background: "#F4D72E",
        opacity: 0.22,
      }}
    />
    <div
      className="color-blob"
      style={{
        top: "55%",
        left: "60%",
        width: 260,
        height: 260,
        background: "#26C44A",
        opacity: 0.14,
      }}
    />
  </>
);

const TopBar = () => (
  <motion.div
    initial={{ y: -20, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ delay: 2.2, duration: 0.5 }}
    className="absolute top-5 sm:top-7 left-0 right-0 z-20 flex justify-center px-4"
  >
    <div
      data-testid="coming-soon-badge"
      className="flex items-center gap-2 bg-[#0B0B0B] text-white font-fredoka font-bold uppercase tracking-[0.25em] text-[10px] sm:text-xs px-4 py-2 rounded-full border-2 border-[#0B0B0B]"
    >
      <span className="w-2 h-2 rounded-full bg-[#26C44A] animate-pulse" />
      Coming soon
    </div>
  </motion.div>
);

const Footer = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 2.4, duration: 0.6 }}
    className="absolute bottom-4 left-0 right-0 text-center z-10 px-4"
  >
    <p className="font-outfit text-[11px] text-[#4A4A4A]">
      © {new Date().getFullYear()} Weepickly — stay pickly, friends.
    </p>
  </motion.div>
);

const ComingSoon = () => {
  return (
    <main
      data-testid="coming-soon-page"
      className="bg-grain relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden"
    >
      <BackgroundDecor />
      <TopBar />

      <section className="relative z-10 w-full max-w-md px-5 pt-20 pb-16 sm:pt-20 sm:pb-14 flex flex-col items-center gap-4 sm:gap-5">
        <LogoAssemble />
        <Tagline />
        <SignupForm />
        <SocialRow />
      </section>

      <Footer />
    </main>
  );
};

export default ComingSoon;
